import { appStorage, appSession } from '../lib/safeStorage';
import { toast } from 'sonner';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Trash2, Minimize2, ThumbsUp, ThumbsDown, Zap, Check, Copy, Loader2, ArrowRight, AlertCircle, Users, FileText, DollarSign, Phone, Calendar, Clock, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import apiClient from '../services/apiClient';
import usePackageRequests from '../hooks/usePackageRequests';
import useUnreadSummary from '../hooks/useUnreadSummary';
import { formatDate } from '../lib/datetime';
import { matchGuide } from '../lib/guideRegistry';
import { startGuide } from '../lib/guideStore';

/**
 * SAgentOrb — the animated "S" mark for the launcher/agent.
 * Visual-only. States: idle (breathing) · greet (soft glow-up) · working
 * (spinning gold ring) · payment (two-tone coin flip). Client-message glow
 * lives on the launcher button itself (mtl-agent-glow), untouched.
 */
const SAgentOrb = ({ agentState, reducedMotion, greetBubble }) => {
  const isPayment = agentState === 'payment';
  const orbMotion = reducedMotion || isPayment
    ? ''
    : agentState === 'greet' ? 'sagent-greet' : 'sagent-idle';
  const flipClass = isPayment && !reducedMotion ? 'sagent-flip' : '';
  return (
    <div className={`sagent-orb ${orbMotion}`} data-agent-state={agentState} data-testid="sagent-orb">
      <div className={`sagent-flipper ${flipClass}`}>
        <div className="sagent-face sagent-face-front">
          <span className="sagent-s sagent-s-gold">S</span>
        </div>
        <div className="sagent-face sagent-face-back">
          <span className="sagent-s sagent-s-dark">S</span>
        </div>
      </div>
      {agentState === 'working' && (
        <span className="sagent-working-ring" data-testid="sagent-working-ring" aria-hidden="true" />
      )}
      {greetBubble && (
        <span className="sagent-bubble" data-testid="sagent-greet-bubble">Need anything?</span>
      )}
    </div>
  );
};

/**
 * SAgentBadge — static dark "home" badge (navy rounded square, gold Playfair S,
 * gold rim). Same look as the launcher's dark side. Used in the panel header.
 */
const SAgentBadge = ({ className, sSize = 18 }) => (
  <div className={`sagent-badge ${className || ''}`} data-testid="sagent-header-badge">
    <span className="sagent-s sagent-s-gold" style={{ fontSize: sSize }}>S</span>
  </div>
);

const AISupportAgent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  // Phase 4 Slice 2 — "What I'd do next" proactive moves, loaded on panel open.
  const [proactiveMoves, setProactiveMoves] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [feedback, setFeedback] = useState({}); // { [messageIndex]: 'up' | 'down' }
  const [copied, setCopied] = useState({}); // { [messageIndex]: true } transient copy confirmation
  const [actionStatus, setActionStatus] = useState({}); // { [messageIndex]: 'running' | 'done' | 'failed' | 'dismissed' }
  // BLOCK S5-4: launcher attention — when the admin engages the agent and then
  // closes/minimises the panel while the agent has posted a response or is
  // waiting on a follow-up, the launcher pulses (reusing the client-message
  // gold glow) + shows a gold dot until the panel is reopened. `engagedRef`
  // gates it to a real in-session ask (never on historical load).
  const [agentAttention, setAgentAttention] = useState(false);
  const engagedRef = useRef(false);
  // BLOCK F1: client messages waiting on the admin — the SAME endpoint the
  // Command Deck card uses. No second count and no new source.
  const [waitingEvents, setWaitingEvents] = useState([]);
  const [totalUnseen, setTotalUnseen] = useState(0);
  const { count: pkgReqCount } = usePackageRequests();
  const [expandedId, setExpandedId] = useState(null);
  // Collapsible top regions — collapsed by default so the chat gets the full
  // window; the user taps a slim bar to drop the list when they want it. The
  // choice is remembered so if they like it open, it stays open.
  const [movesOpen, setMovesOpen] = useState(() => { try { return appStorage.getItem('sagentMovesOpen') === '1'; } catch { return false; } });
  const [waitingOpen, setWaitingOpen] = useState(() => { try { return appStorage.getItem('sagentWaitingOpen') === '1'; } catch { return false; } });
  const toggleMoves = () => setMovesOpen((v) => { const n = !v; try { appStorage.setItem('sagentMovesOpen', n ? '1' : '0'); } catch (e) {} return n; });
  const toggleWaiting = () => setWaitingOpen((v) => { const n = !v; try { appStorage.setItem('sagentWaitingOpen', n ? '1' : '0'); } catch (e) {} return n; });
  const [handlingId, setHandlingId] = useState(null);
  // S39 — suggested reply state (expanded pinned card only).
  const [drafts, setDrafts] = useState({});       // key -> { status:'loading'|'ready'|'none', text }
  const [sendState, setSendState] = useState({});  // key -> 'sending'|'sent'|'error'
  const [editingKey, setEditingKey] = useState(null);
  const draftFetchedRef = useRef(new Set());
  const draftTextareaRef = useRef(null);
  const announcedRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const getAnnounced = () => {
    if (announcedRef.current) return announcedRef.current;
    let set = new Set();
    try { const raw = appSession.getItem('staffed_agent_announced_v1'); if (raw) set = new Set(JSON.parse(raw)); } catch { /* off */ }
    announcedRef.current = set;
    return set;
  };
  const saveAnnounced = (set) => { try { appSession.setItem('staffed_agent_announced_v1', JSON.stringify([...set])); } catch { /* off */ } };

  const showMessageBanner = React.useCallback((ev, moreCount) => {
    const firstLine = (ev.preview || '').split('\n')[0];
    toast.custom((t) => (
      <div role="status" aria-live="polite" style={{ width: 'min(520px, 100vw - 24px)' }}
        className="rounded-[14px] border border-[#D4AF37]/40 bg-gradient-to-b from-[#141a2b] to-[#0B0F1A] shadow-2xl px-4 py-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#E5C76B] flex items-center justify-center text-[#0B0F1A] font-bold shrink-0">S</div>
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-semibold truncate">{ev.clientName} sent a message{moreCount > 0 ? ` · +${moreCount} more` : ''}</p>
          {firstLine && <p className="text-white/50 text-xs truncate">{firstLine}</p>}
        </div>
        <button onClick={() => { window.dispatchEvent(new CustomEvent('staffed:assistant:open')); toast.dismiss(t); }}
          className="text-[#D4AF37] text-sm font-semibold px-2 shrink-0">Reply</button>
        <button aria-label="Dismiss" onClick={() => toast.dismiss(t)} className="text-white/40 hover:text-white text-lg leading-none px-1 shrink-0">×</button>
      </div>
    ), { duration: 12000, position: 'top-center' });
  }, []);

  const _unread = useUnreadSummary();
  const loadWaiting = _unread.refresh;

  // S76 — the data now comes from the ONE shared source (fetched once on load,
  // refreshed on a single interval + debounced window events). This effect only
  // mirrors it into local state and runs the "banner once per unseen" logic
  // when the shared data changes; it no longer fetches.
  useEffect(() => {
    const evs = _unread.events || [];
    setWaitingEvents(evs);
    setTotalUnseen(_unread.totalUnseen || 0);
    const announced = getAnnounced();
    const fresh = evs.filter((e) => e.unseen && !announced.has(`${e.eventId}|${e.waitingSince}`));
    if (fresh.length) {
      fresh.forEach((e) => announced.add(`${e.eventId}|${e.waitingSince}`));
      saveAnnounced(announced);
      showMessageBanner(fresh[0], fresh.length - 1); // events are newest-first
    }
  }, [_unread.events, _unread.totalUnseen, showMessageBanner]);
  const waitingCount = waitingEvents.length;
  // Phase 4 Slice 3 — high-value moves (overdue money / understaffed event, the
  // red-toned ones) drive the launcher badge so an urgent opportunity is visible
  // before S is opened. Amber moves stay in the card, off the launcher.
  const highValueMoveCount = proactiveMoves.filter((m) => m && m.impactTone === 'red').length;
  // The card that is actually expanded (first is open by default).
  const effectiveExpandedId = expandedId || (waitingEvents[0] && waitingEvents[0].eventId) || null;

  // S39 — render the suggested-reply region for an expanded pinned card.
  const renderSuggestedReply = (ev) => {
    const key = `${ev.eventId}|${ev.waitingSince}`;
    const firstName = ((ev.clientName || 'there').trim().split(/\s+/)[0]) || 'there';
    const d = drafts[key];
    const send = sendState[key];
    const isEditing = editingKey === key;

    // Fallback S38 two-button row — draft null / error / not yet ready.
    const twoButtons = (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => { setIsOpen(false); navigate(`/event/${ev.eventId}?focus=messages`); }}
          className="flex-1 h-11 rounded-lg bg-[#D4AF37] hover:bg-[#E5C76B] text-[#0B0F1A] text-[12px] font-semibold transition inline-flex items-center justify-center gap-1"
          data-testid={`ai-agent-open-workspace-${ev.eventId}`}
        >
          Open in workspace <ArrowRight className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          disabled={handlingId === ev.eventId}
          onClick={() => handleMarkHandled(ev.eventId)}
          className="flex-1 h-11 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 text-white/80 text-[12px] font-semibold transition inline-flex items-center justify-center gap-1 disabled:opacity-50"
          data-testid={`ai-agent-mark-handled-${ev.eventId}`}
        >
          {handlingId === ev.eventId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Mark handled
        </button>
      </div>
    );

    if (send === 'sent') {
      return (
        <div
          className="h-11 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 w-full"
          data-testid={`ai-agent-sent-${ev.eventId}`}
        >
          <Check className="w-4 h-4" /> Sent to {firstName}
        </div>
      );
    }

    // Loading — quiet one-line skeleton, ~96px reserved so nothing jumps.
    if (!d || d.status === 'loading') {
      return (
        <div className="flex items-center px-1" style={{ minHeight: 96 }} data-testid={`ai-agent-draft-loading-${ev.eventId}`}>
          <span className="text-[12px] text-white/40 inline-flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Drafting a reply…
          </span>
        </div>
      );
    }

    // No draft — silent fall back to the S38 buttons, no error noise.
    if (d.status !== 'ready') {
      return twoButtons;
    }

    const canSend = (d.text || '').trim().length > 0 && send !== 'sending';
    return (
      <div className="space-y-2" data-testid={`ai-agent-draft-box-${ev.eventId}`}>
        <div className="rounded-lg border border-dashed border-[#D4AF37]/45 bg-[#D4AF37]/[0.04] p-2.5 space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37]/85 font-semibold">
              {isEditing ? 'Editing…' : 'Suggested reply — you send it'}
            </span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label="About this suggestion"
                  className="w-4 h-4 rounded-full border border-white/25 text-white/50 text-[9px] font-bold leading-none inline-flex items-center justify-center hover:text-white hover:border-white/50 transition"
                  data-testid={`ai-agent-draft-info-${ev.eventId}`}
                >i</button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px] text-[11px] leading-relaxed">
                The assistant wrote this from this event's own messages. It never sends on its own, and it won't promise prices, dates or availability — edit it freely.
              </TooltipContent>
            </Tooltip>
          </div>
          <textarea
            ref={(el) => { if (isEditing) draftTextareaRef.current = el; autoSizeTextarea(el, isEditing ? 8 : 5); }}
            readOnly={!isEditing}
            value={d.text}
            onChange={(e) => {
              const v = e.target.value;
              setDrafts((prev) => ({ ...prev, [key]: { ...prev[key], text: v } }));
              autoSizeTextarea(e.target, isEditing ? 8 : 5);
            }}
            rows={2}
            className="w-full bg-transparent text-[12px] text-white/90 resize-none outline-none leading-[20px]"
            style={{ maxHeight: isEditing ? 160 : 100 }}
            data-testid={`ai-agent-draft-text-${ev.eventId}`}
          />
        </div>
        <button
          type="button"
          disabled={!canSend}
          onClick={() => handleSendDraft(ev, firstName)}
          className="w-full h-11 rounded-lg bg-[#D4AF37] hover:bg-[#E5C76B] text-[#0B0F1A] text-[12px] font-bold transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid={`ai-agent-send-draft-${ev.eventId}`}
        >
          {send === 'sending' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send this reply to {firstName}
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => startEditDraft(key)}
            className="flex-1 h-11 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 text-white/80 text-[12px] font-semibold transition"
            data-testid={`ai-agent-edit-draft-${ev.eventId}`}
          >
            Edit first
          </button>
          <button
            type="button"
            onClick={() => { setIsOpen(false); navigate(`/event/${ev.eventId}?focus=messages`); }}
            className="flex-1 h-11 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 text-white/80 text-[12px] font-semibold transition"
            data-testid={`ai-agent-open-workspace-${ev.eventId}`}
          >
            Open in workspace
          </button>
        </div>
        {send === 'error' && (
          <p className="text-[11px] text-red-400" data-testid={`ai-agent-send-error-${ev.eventId}`}>
            Couldn't send — try again, or open the workspace.
          </p>
        )}
        <p className="text-[11px] text-white/40 text-center">Nothing is sent until you press Send.</p>
        <button
          type="button"
          disabled={handlingId === ev.eventId}
          onClick={() => handleMarkHandled(ev.eventId)}
          className="w-full text-[11px] text-white/40 hover:text-white/70 underline underline-offset-2 transition disabled:opacity-50"
          data-testid={`ai-agent-mark-handled-${ev.eventId}`}
        >
          Already answered by phone? Mark handled
        </button>
      </div>
    );
  };

  // S38 — SEEN: while the panel is open (pinned region rendered), mark every
  // unseen event read once, then reload. Never while closed or minimized/tucked.
  useEffect(() => {
    if (!isOpen || isMinimized) return;
    const unseen = waitingEvents.filter((e) => e.unseen);
    if (!unseen.length) return;
    (async () => {
      for (const e of unseen) {
        try { await apiClient.patch(`/messages/event/${e.eventId}/read`); } catch { /* ignore */ }
      }
      loadWaiting();
    })();
  }, [isOpen, isMinimized, waitingEvents, loadWaiting]);

  const handleMarkHandled = async (eventId) => {
    setHandlingId(eventId);
    try {
      await apiClient.post(`/messages/event/${eventId}/handled`, { reason: 'Answered outside STAFFED' });
      window.dispatchEvent(new CustomEvent('mtl:client-message-handled', { detail: { eventId } }));
      await loadWaiting();
    } catch { /* ignore */ } finally { setHandlingId(null); }
  };

  // ── S39 — suggested reply (assistant-drafted, admin sends) ──
  const draftKeyFor = (ev) => `${ev.eventId}|${ev.waitingSince}`;

  const autoSizeTextarea = (el, maxLines = 8) => {
    if (!el) return;
    el.style.height = 'auto';
    const max = maxLines * 20; // 20px line-height per line, then scroll
    const h = Math.min(el.scrollHeight, max);
    el.style.height = `${h}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  };

  const fetchDraft = React.useCallback(async (ev) => {
    const key = `${ev.eventId}|${ev.waitingSince}`;
    if (draftFetchedRef.current.has(key)) return; // once per waiting message
    draftFetchedRef.current.add(key);
    setDrafts((d) => ({ ...d, [key]: { status: 'loading', text: '' } }));
    try {
      const res = await apiClient.post(`/messages/event/${ev.eventId}/suggest-reply`);
      const draft = res.data?.draft;
      if (draft && String(draft).trim()) {
        setDrafts((d) => ({ ...d, [key]: { status: 'ready', text: String(draft) } }));
      } else {
        setDrafts((d) => ({ ...d, [key]: { status: 'none', text: '' } }));
      }
    } catch {
      setDrafts((d) => ({ ...d, [key]: { status: 'none', text: '' } }));
    }
  }, []);

  // S39 — fetch a suggested reply ONCE per waiting message, only for the
  // expanded card while the panel is open. Never on the 30s poll (loadWaiting
  // does not call this), never for collapsed cards.
  useEffect(() => {
    if (!isOpen || isMinimized || !effectiveExpandedId) return;
    const ev2 = waitingEvents.find((e) => e.eventId === effectiveExpandedId);
    if (ev2) fetchDraft(ev2);
  }, [isOpen, isMinimized, effectiveExpandedId, waitingEvents, fetchDraft]);

  const startEditDraft = (key) => {
    setEditingKey(key);
    setTimeout(() => {
      const el = draftTextareaRef.current;
      if (el) { el.focus(); const len = el.value.length; el.setSelectionRange(len, len); }
    }, 0);
  };

  const handleSendDraft = async (ev, firstName) => {
    const key = `${ev.eventId}|${ev.waitingSince}`;
    const text = (drafts[key]?.text || '').trim();
    if (!text || sendState[key] === 'sending') return; // guard double send + empty
    setSendState((s) => ({ ...s, [key]: 'sending' }));
    try {
      await apiClient.post(`/messages/event/${ev.eventId}/reply`, { message: text, draftedByAI: true });
      setSendState((s) => ({ ...s, [key]: 'sent' }));
      // Show "Sent" for ~2.5s, then clear count + glow everywhere.
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('mtl:client-message-handled', { detail: { eventId: ev.eventId } }));
        loadWaiting();
      }, 2500);
    } catch {
      setSendState((s) => ({ ...s, [key]: 'error' })); // keep typed text, re-enable
    }
  };

  // BLOCK S7 — the Help panel and How-To card open the assistant here, with an
  // optional prefilled question ("Ask STAFFED" / "Ask about this step").
  useEffect(() => {
    const onOpen = (e) => {
      setIsOpen(true);
      setIsMinimized(false);
      const prefill = e && e.detail && e.detail.prefill;
      if (prefill) setInputValue(prefill);
    };
    window.addEventListener('staffed:assistant:open', onOpen);
    return () => window.removeEventListener('staffed:assistant:open', onOpen);
  }, []);

  // ── BLOCK S37 — draggable / tuckable launcher (>= 640px only) ──────────
  const LAUNCHER_KEY = 'staffed_agent_launcher_v1';
  const L_SIZE = 44, L_MARGIN = 12, L_DEFAULT_OFFSET = 24, L_DRAG_THRESHOLD = 5, L_TUCK_ZONE = 30, TAB_W = 14, TAB_H = 64;
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 640 : false);
  const [lPos, setLPos] = useState(null);        // {left, top} px (desktop only)
  const [tucked, setTucked] = useState(null);    // null | 'l' | 'r'
  const [dragging, setDragging] = useState(false);
  const [tuckHint, setTuckHint] = useState(null);// null | 'l' | 'r' while dragging near edge
  const [reducedMotion] = useState(() => typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false);

  // ── S-agent motion state machine (visual only) ──────────────────────────
  // idle | greet | working | payment. Client-message pulse stays on the button
  // (mtl-agent-glow) and is independent of this. Priority: working/payment are
  // never interrupted by a greet.
  const [agentState, setAgentState] = useState('idle');
  const [greetBubble, setGreetBubble] = useState(false);
  const agentTimerRef = useRef(null);
  const bubbleTimerRef = useRef(null);

  const triggerGreet = React.useCallback(() => {
    setAgentState((s) => (s === 'working' || s === 'payment') ? s : 'greet');
    setGreetBubble(true);
    clearTimeout(agentTimerRef.current);
    clearTimeout(bubbleTimerRef.current);
    agentTimerRef.current = setTimeout(() => setAgentState((s) => (s === 'greet' ? 'idle' : s)), 1400);
    bubbleTimerRef.current = setTimeout(() => setGreetBubble(false), 2500);
  }, []);

  // WORKING follows the agent's own drafting/loading flag (no logic change).
  useEffect(() => {
    if (isLoading) setAgentState('working');
    else setAgentState((s) => (s === 'working' ? 'idle' : s));
  }, [isLoading]);

  // GREET on landing on a page.
  useEffect(() => {
    triggerGreet();
  }, [location.pathname, triggerGreet]);

  // S5-4: launcher attention. Clear the moment the panel is open. When it's
  // closed after a real engagement and the last message is from the agent (a
  // posted response OR an awaiting-follow-up question), pulse the launcher.
  useEffect(() => {
    if (isOpen) { setAgentAttention(false); return; }
    if (!engagedRef.current) return;
    const last = messages[messages.length - 1];
    if (last && last.role === 'assistant') setAgentAttention(true);
  }, [isOpen, messages]);

  // Call times show in 12-hour with AM/PM — never 24-hour — everywhere the
  // agent surfaces one (matches the rest of the app).
  const to12h = React.useCallback((t) => {
    if (!t) return '';
    const s = String(t).trim();
    const m = s.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
    if (!m) return s;
    let h = parseInt(m[1], 10);
    const min = m[2];
    const ap = m[3];
    if (ap) return `${((h % 12) || 12)}:${min} ${ap.toUpperCase()}`;
    const suffix = h >= 12 ? 'PM' : 'AM';
    h = (h % 12) || 12;
    return `${h}:${min} ${suffix}`;
  }, []);

  // PAYMENT WIN — the coin flip. Fires on the window event so the payment/
  // deposit flow can trigger it without this component owning that logic.
  useEffect(() => {
    const onWin = () => {
      setAgentState((s) => (s === 'working' ? s : 'payment'));
      clearTimeout(agentTimerRef.current);
      agentTimerRef.current = setTimeout(() => setAgentState((s) => (s === 'payment' ? 'idle' : s)), 5100);
    };
    window.addEventListener('staffed:agent:payment-win', onWin);
    return () => window.removeEventListener('staffed:agent:payment-win', onWin);
  }, []);

  useEffect(() => () => { clearTimeout(agentTimerRef.current); clearTimeout(bubbleTimerRef.current); }, []);

  // ── Resizable panel (desktop nicety) ────────────────────────────────────
  // Saved size (per user) so it reopens at the size the user pulled it to.
  // Mobile keeps its full-width layout — resize is gated to desktop.
  const PANEL_MIN_W = 384, PANEL_MIN_H = 500;
  const panelUserId = useMemo(() => {
    try {
      const u = JSON.parse(appStorage.getItem('user') || appSession.getItem('user') || 'null');
      return (u && (u.id || u._id || u.email)) || 'anon';
    } catch (_) { return 'anon'; }
  }, []);
  const [panelSize, setPanelSize] = useState(() => {
    try {
      const raw = appStorage.getItem(`sagentPanelSize:${panelUserId}`);
      const s = raw ? JSON.parse(raw) : null;
      return (s && s.w && s.h) ? s : null;
    } catch (_) { return null; }
  });
  // Live box while/after dragging within a session ({left,top,width,height}).
  const [resizeBox, setResizeBox] = useState(null);
  const [resizing, setResizing] = useState(false);
  const resizeRef = useRef(null);
  // Saved position (per user) so the panel reopens where the admin left it.
  const [panelPos, setPanelPos] = useState(() => {
    try {
      const raw = appStorage.getItem(`sagentPanelPos:${panelUserId}`);
      const p = raw ? JSON.parse(raw) : null;
      return (p && typeof p.left === 'number' && typeof p.top === 'number') ? p : null;
    } catch (_) { return null; }
  });
  const persistPos = (box) => {
    const pos = { left: Math.round(box.left), top: Math.round(box.top) };
    setPanelPos(pos);
    try { appStorage.setItem(`sagentPanelPos:${panelUserId}`, JSON.stringify(pos)); } catch (_) { /* storage off */ }
  };

  const onResizeMove = React.useCallback((e) => {
    const s = resizeRef.current;
    if (!s) return;
    const rect = s.rect;
    const corner = s.corner;
    const maxW = Math.max(PANEL_MIN_W, window.innerWidth - 32);
    const maxH = Math.max(PANEL_MIN_H, window.innerHeight - 32);
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    let width, left, height, top;
    if (corner.includes('l')) { width = clamp(rect.width - dx, PANEL_MIN_W, maxW); left = rect.right - width; }
    else { width = clamp(rect.width + dx, PANEL_MIN_W, maxW); left = rect.left; }
    if (corner.includes('t')) { height = clamp(rect.height - dy, PANEL_MIN_H, maxH); top = rect.bottom - height; }
    else { height = clamp(rect.height + dy, PANEL_MIN_H, maxH); top = rect.top; }
    left = Math.max(16, Math.min(left, window.innerWidth - width - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - height - 16));
    setResizeBox({ left, top, width, height });
  }, []);

  const onResizeEnd = React.useCallback(() => {
    window.removeEventListener('pointermove', onResizeMove);
    window.removeEventListener('pointerup', onResizeEnd);
    setResizing(false);
    setResizeBox((box) => {
      if (box) {
        const size = { w: Math.round(box.width), h: Math.round(box.height) };
        setPanelSize(size);
        try { appStorage.setItem(`sagentPanelSize:${panelUserId}`, JSON.stringify(size)); } catch (_) { /* storage off */ }
        persistPos(box);
      }
      return box;
    });
    resizeRef.current = null;
  }, [onResizeMove, panelUserId]);

  const startResize = React.useCallback((e, corner) => {
    if (!isDesktop || isMinimized) return;
    e.preventDefault();
    e.stopPropagation();
    const panel = e.currentTarget.closest('[data-testid="ai-agent-window"]');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    resizeRef.current = { corner, rect, startX: e.clientX, startY: e.clientY };
    setResizing(true);
    setResizeBox({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    window.addEventListener('pointermove', onResizeMove);
    window.addEventListener('pointerup', onResizeEnd);
  }, [isDesktop, isMinimized, onResizeMove, onResizeEnd]);

  // ── Move the whole panel by dragging its header bar ─────────────────────
  const moveRef = useRef(null);
  const onPanelMove = React.useCallback((e) => {
    const s = moveRef.current;
    if (!s) return;
    const { rect, startX, startY } = s;
    let left = rect.left + (e.clientX - startX);
    let top = rect.top + (e.clientY - startY);
    left = Math.max(16, Math.min(left, window.innerWidth - rect.width - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - rect.height - 16));
    setResizeBox({ left, top, width: rect.width, height: rect.height });
  }, []);
  const onPanelMoveEnd = React.useCallback(() => {
    window.removeEventListener('pointermove', onPanelMove);
    window.removeEventListener('pointerup', onPanelMoveEnd);
    setResizing(false);
    setResizeBox((box) => { if (box) persistPos(box); return box; });
    moveRef.current = null;
  }, [onPanelMove]); // eslint-disable-line react-hooks/exhaustive-deps
  const startPanelMove = React.useCallback((e) => {
    if (!isDesktop || isMinimized) return;
    // Only from empty header space — never from a control/handle.
    if (e.target.closest('button, a, input, textarea, [data-no-drag]')) return;
    e.preventDefault();
    const panel = e.currentTarget.closest('[data-testid="ai-agent-window"]');
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    moveRef.current = { rect, startX: e.clientX, startY: e.clientY };
    setResizing(true);
    setResizeBox({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    window.addEventListener('pointermove', onPanelMove);
    window.addEventListener('pointerup', onPanelMoveEnd);
  }, [isDesktop, isMinimized, onPanelMove, onPanelMoveEnd]);

  // Re-anchor (bottom-right) at the saved SIZE on each fresh open.
  useEffect(() => { if (!isOpen || isMinimized) setResizeBox(null); }, [isOpen, isMinimized]);

  const launcherRef = useRef(null);
  const lPosRef = useRef(null);
  const ratioRef = useRef({ xRatio: null, yRatio: null });
  const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, offX: 0, offY: 0, pointerId: null });
  const suppressClickRef = useRef(false);
  useEffect(() => { lPosRef.current = lPos; }, [lPos]);

  const clampPos = (left, top) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const maxL = Math.max(L_MARGIN, vw - L_SIZE - L_MARGIN);
    const maxT = Math.max(L_MARGIN, vh - L_SIZE - L_MARGIN);
    return { left: Math.min(Math.max(left, L_MARGIN), maxL), top: Math.min(Math.max(top, L_MARGIN), maxT) };
  };
  const defaultPos = () => clampPos(window.innerWidth - L_DEFAULT_OFFSET - L_SIZE, window.innerHeight - L_DEFAULT_OFFSET - L_SIZE);
  const persistLauncher = (xRatio, yRatio, tk) => {
    try { appStorage.setItem(LAUNCHER_KEY, JSON.stringify({ xRatio, yRatio, tucked: tk || null })); } catch { /* storage off */ }
  };
  const commitPos = (p, tk) => {
    const xRatio = p.left / window.innerWidth, yRatio = p.top / window.innerHeight;
    ratioRef.current = { xRatio, yRatio };
    setLPos(p);
    persistLauncher(xRatio, yRatio, tk);
  };

  // Restore on mount (desktop only).
  useEffect(() => {
    if (typeof window === 'undefined' || window.innerWidth < 640) return;
    let saved = null;
    try { const raw = appStorage.getItem(LAUNCHER_KEY); saved = raw ? JSON.parse(raw) : null; } catch { saved = null; }
    if (saved && typeof saved === 'object' && saved.xRatio != null) {
      ratioRef.current = { xRatio: saved.xRatio, yRatio: saved.yRatio };
      setTucked(saved.tucked === 'l' || saved.tucked === 'r' ? saved.tucked : null);
      setLPos(clampPos(saved.xRatio * window.innerWidth, saved.yRatio * window.innerHeight));
    } else {
      const p = defaultPos();
      ratioRef.current = { xRatio: p.left / window.innerWidth, yRatio: p.top / window.innerHeight };
      setLPos(p);
    }
  }, []);

  // Resize: keep on-screen, recompute from ratios.
  useEffect(() => {
    const onResize = () => {
      const desk = window.innerWidth >= 640;
      setIsDesktop(desk);
      if (!desk) return;
      if (ratioRef.current.xRatio == null) { setLPos(defaultPos()); return; }
      setLPos(clampPos(ratioRef.current.xRatio * window.innerWidth, ratioRef.current.yRatio * window.innerHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onLauncherPointerDown = (e) => {
    if (window.innerWidth < 640) return;
    const rect = launcherRef.current.getBoundingClientRect();
    dragRef.current = { active: true, moved: false, startX: e.clientX, startY: e.clientY, offX: e.clientX - rect.left, offY: e.clientY - rect.top, pointerId: e.pointerId };
    try { launcherRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  const onLauncherPointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) < L_DRAG_THRESHOLD) return;
    if (!d.moved) { d.moved = true; setDragging(true); if (tucked) setTucked(null); }
    const p = clampPos(e.clientX - d.offX, e.clientY - d.offY);
    setLPos(p);
    const nearL = p.left <= L_MARGIN + L_TUCK_ZONE;
    const nearR = p.left >= window.innerWidth - L_SIZE - L_MARGIN - L_TUCK_ZONE;
    setTuckHint(nearL ? 'l' : nearR ? 'r' : null);
  };
  const onLauncherPointerUp = (e) => {
    const d = dragRef.current;
    try { launcherRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (!d.active) return;
    d.active = false;
    if (!d.moved) return;                       // a click — handled by onClick
    suppressClickRef.current = true;            // a real drag must never open
    setDragging(false);
    const cur = lPosRef.current || clampPos(e.clientX - d.offX, e.clientY - d.offY);
    const tk = (tuckHint === 'l' || tuckHint === 'r') ? tuckHint : null;
    setTuckHint(null);
    if (tk) {
      const left = tk === 'l' ? 0 : window.innerWidth - TAB_W;
      const finalP = { left, top: clampPos(cur.left, cur.top).top };
      setTucked(tk);
      commitPos(finalP, tk);
    } else {
      setTucked(null);
      commitPos(clampPos(cur.left, cur.top), null);
    }
  };
  const onLauncherClick = () => {
    if (suppressClickRef.current) { suppressClickRef.current = false; return; }
    if (tucked) {                               // un-tuck without opening
      const left = tucked === 'l' ? L_MARGIN : window.innerWidth - L_SIZE - L_MARGIN;
      const top = (lPosRef.current ? clampPos(lPosRef.current.left, lPosRef.current.top).top : defaultPos().top);
      setTucked(null);
      commitPos(clampPos(left, top), null);
      return;
    }
    setIsOpen(true);
  };
  const onLauncherKeyDown = (e) => {
    if (window.innerWidth < 640) return;
    const step = 24;
    let { left, top } = lPosRef.current || defaultPos();
    if (e.key === 'ArrowLeft') left -= step;
    else if (e.key === 'ArrowRight') left += step;
    else if (e.key === 'ArrowUp') top -= step;
    else if (e.key === 'ArrowDown') top += step;
    else return;
    e.preventDefault();
    if (tucked) setTucked(null);
    commitPos(clampPos(left, top), null);
  };

  // Chat window anchored near the launcher (desktop only).
  const computeWindowPos = (mini, fullH) => {
    const vw = window.innerWidth, vh = window.innerHeight;
    const winW = mini ? 288 : 384;
    const winH = mini ? 220 : (fullH || 500);
    const base = lPosRef.current || defaultPos();
    const lw = tucked ? TAB_W : L_SIZE;
    const lx = tucked ? (tucked === 'l' ? 0 : vw - TAB_W) : base.left;
    const ly = base.top;
    let left = (lx + lw / 2) > vw / 2 ? (lx + lw - winW) : lx;
    let top = (ly + L_SIZE / 2) > vh / 2 ? (ly + L_SIZE - winH) : ly;
    left = Math.min(Math.max(left, L_MARGIN), Math.max(L_MARGIN, vw - winW - L_MARGIN));
    top = Math.min(Math.max(top, L_MARGIN), Math.max(L_MARGIN, vh - winH - L_MARGIN));
    return { left, top, width: winW };
  };
  const launcherAtDefault = () => {
    if (!lPos) return true;
    const dp = defaultPos();
    return Math.abs(lPos.left - dp.left) <= 2 && Math.abs(lPos.top - dp.top) <= 2;
  };


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
      // Load chat history when opening
      loadChatHistory();
      loadProactiveMoves();
    }
  }, [isOpen, isMinimized]);
  // Phase 4 Slice 3 — the launcher badge needs the moves even while the panel is
  // closed, so refresh them on mount and on a slow poll (the open effect above
  // already refreshes on open for the card).
  useEffect(() => {
    loadProactiveMoves();
    const _movesTimer = setInterval(loadProactiveMoves, 120000);
    return () => clearInterval(_movesTimer);
  }, []);

  const loadChatHistory = async () => {
    try {
      const response = await apiClient.get('/ai-agent/history?limit=30');

      if (response.data && response.data.length > 0) {
        setMessages(response.data.map(msg => ({
          role: msg.role,
          content: msg.content
        })));
        setSessionId(response.data[0]?.sessionId);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  // Phase 4 Slice 2 — pull the ranked next-best moves when the panel opens.
  const loadProactiveMoves = async () => {
    try {
      const res = await apiClient.get('/ai-agent/proactive-moves');
      setProactiveMoves(Array.isArray(res.data?.moves) ? res.data.moves : []);
    } catch { setProactiveMoves([]); }
  };
  const clearHistory = async () => {
    try {
      await apiClient.delete('/ai-agent/history');

      setMessages([]);
      setSessionId(null);
    } catch (error) {
      console.error('Failed to clear history:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    engagedRef.current = true;  // real in-session ask — enables launcher attention
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const user = appStorage.getItem('user') || appSession.getItem('user');
      if (!user) {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: 'Please log in to use the AI Support Agent.' 
        }]);
        setIsLoading(false);
        return;
      }

      const response = await apiClient.post(
        '/ai-agent/chat',
        { message: userMessage, sessionId }
      );

      setSessionId(response.data.sessionId);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        proposedAction: response.data.proposedAction || null,
        proposedPlan: response.data.proposedPlan || null,
        startedJobs: response.data.startedJobs || null,
        standingJobs: response.data.standingJobs || null,
      }]);
    } catch (error) {
      console.error('AI Agent error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'I apologize, but I encountered an issue. Please try again in a moment.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const copyMessage = async (idx, text) => {
    const value = String(text || '')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      .replace(/\*\*(.+?)\*\*/g, '$1')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1$2')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^\s{0,3}#{1,4}\s+/gm, '')
      .replace(/^\s*[-*•]\s+/gm, '• ')
      .replace(/^\s*(?:---+|\*\*\*+)\s*$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    }
    setCopied((prev) => ({ ...prev, [idx]: true }));
    setTimeout(() => setCopied((prev) => { const n = { ...prev }; delete n[idx]; return n; }), 1600);
  };
  const sendFeedback = async (idx, rating) => {
    if (feedback[idx]) return;
    setFeedback((prev) => ({ ...prev, [idx]: rating }));
    try {
      await apiClient.post('/ai-agent/feedback', {
        rating,
        question: messages[idx - 1]?.content || '',
        answer: messages[idx]?.content || '',
        sessionId,
      });
    } catch (e) { /* non-fatal */ }
  };

  // Confirm-first action loop: nothing runs until the user taps Confirm here.
  const executeAction = async (idx, action) => {
    if (!action || actionStatus[idx]) return;
    engagedRef.current = true;
    setActionStatus((prev) => ({ ...prev, [idx]: 'running' }));
    try {
      const res = await apiClient.post('/ai-agent/action', {
        action_type: action.type,
        params: action.params || {},
      });
      const result = res.data || {};
      setActionStatus((prev) => ({ ...prev, [idx]: result.success ? 'done' : 'failed' }));
      setMessages((prev) => [...prev, {
        role: 'assistant',
        fromAction: true,
        content: result.success
          ? `✓ ${result.message || 'Done.'}`
          : `I couldn't complete that: ${result.message || 'please try again.'}`,
      }]);
    } catch (e) {
      setActionStatus((prev) => ({ ...prev, [idx]: 'failed' }));
      setMessages((prev) => [...prev, {
        role: 'assistant',
        fromAction: true,
        content: "I couldn't complete that action just now. Please try again in a moment.",
      }]);
    }
  };

  // Phase 4 Slice 2 — a tapped move routes into a flow the user already knows:
  //  • kind "open"   → close the panel and jump to the event (optional section)
  //  • kind "prompt" → post the text as a normal chat ask (S drafts the reply)
  //  • kind "action" → drop the SAME confirm card the chat uses (Phase 3), so
  //                    nothing fires until the user confirms.
  const postMovePrompt = async (text) => {
    if (!text || !text.trim() || isLoading) return;
    engagedRef.current = true;
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setIsLoading(true);
    try {
      const response = await apiClient.post('/ai-agent/chat', { message: text, sessionId });
      setSessionId(response.data.sessionId);
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: response.data.response,
        proposedAction: response.data.proposedAction || null,
        proposedPlan: response.data.proposedPlan || null,
        startedJobs: response.data.startedJobs || null,
        standingJobs: response.data.standingJobs || null,
      }]);
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'assistant', content: 'I apologize, but I encountered an issue. Please try again in a moment.' }]);
    } finally {
      setIsLoading(false);
    }
  };
  const runMoveAction = (move, action) => {
    if (!action) return;
    engagedRef.current = true;
    if (action.kind === 'open') {
      setIsOpen(false);
      const sec = action.section ? `?section=${action.section}` : '';
      navigate(`/event/${action.eventId}${sec}`);
      return;
    }
    if (action.kind === 'prompt') {
      postMovePrompt(action.text || '');
      return;
    }
    if (action.kind === 'action') {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: move.title + '.',
        proposedAction: { type: action.type, params: action.params || {} },
      }]);
    }
  };
  const dismissAction = (idx) => {
    setActionStatus((prev) => ({ ...prev, [idx]: 'dismissed' }));
  };

  // ── Multi-step PLAN runner (Phase 1b) ───────────────────────────────────
  // planState[idx] = { phase: 'proposed'|'running'|'failed'|'done',
  //                    steps: { [stepIdx]: 'pending'|'running'|'done'|'failed'|'skipped' },
  //                    failReason }
  const [planState, setPlanState] = useState({});
  const [autopilotCtl, setAutopilotCtl] = useState({}); // { [jobId]: { runState, removed, open } }

  const dismissPlan = (idx) => {
    setPlanState((prev) => ({ ...prev, [idx]: { ...(prev[idx] || {}), phase: 'dismissed' } }));
  };
  const controlStandingJob = async (jobId, op) => {
    try {
      const res = await apiClient.post(`/ai-agent/standing-jobs/${jobId}/${op}`);
      const st = res.data?.status;
      setAutopilotCtl((p) => ({ ...p, [jobId]: { ...(p[jobId] || {}), runState: st, removed: st === 'stopped' } }));
    } catch (e) {
      toast.error("Couldn't update that job just now.");
    }
  };
  const toggleJobActivity = (jobId) => {
    setAutopilotCtl((p) => ({ ...p, [jobId]: { ...(p[jobId] || {}), open: !(p[jobId]?.open) } }));
  };
  const sendReminderNow = async (jobId) => {
    try {
      await apiClient.post(`/ai-agent/standing-jobs/${jobId}/send-now`);
      setAutopilotCtl((p) => ({ ...p, [jobId]: { ...(p[jobId] || {}), sentNow: true } }));
      toast.success('Reminder sent to the client.');
    } catch (e) {
      toast.error("Couldn't send that reminder just now.");
    }
  };

  // Confirm plan → run ONLY the client:false steps, in order, awaiting each.
  // Stop on the first failure; never run client:true steps here — those are
  // surfaced afterwards as their own single-action confirm cards.
  const executePlan = async (idx, plan) => {
    if (!plan || (planState[idx] && planState[idx].phase && planState[idx].phase !== 'proposed')) return;
    engagedRef.current = true;
    const steps = plan.steps || [];
    const initSteps = {};
    steps.forEach((_, i) => { initSteps[i] = 'pending'; });
    setPlanState((prev) => ({ ...prev, [idx]: { phase: 'running', steps: initSteps, failReason: '' } }));

    let failedAt = -1;
    let failReason = '';
    // Carry the most-recent created event forward so a later step can act on
    // the record an earlier step created (its real id doesn't exist at plan time).
    let carriedEventId = '';
    let carriedEventName = '';
    const withCarriedIds = (params) => {
      const p = { ...(params || {}) };
      if (carriedEventId) {
        if ('eventId' in p) p.eventId = carriedEventId;
        if ('eventName' in p) p.eventName = carriedEventName;
      }
      return p;
    };
    for (let i = 0; i < steps.length; i += 1) {
      const step = steps[i];
      if (step.client) continue; // client sends never run as part of Confirm plan
      setPlanState((prev) => ({ ...prev, [idx]: { ...prev[idx], steps: { ...prev[idx].steps, [i]: 'running' } } }));
      try {
        const res = await apiClient.post('/ai-agent/action', { action_type: step.type, params: withCarriedIds(step.params) });
        const result = res.data || {};
        if (result.success) {
          if (result.data && result.data.eventId) {
            carriedEventId = result.data.eventId;
            carriedEventName = result.data.name || carriedEventName;
          }
          setPlanState((prev) => ({ ...prev, [idx]: { ...prev[idx], steps: { ...prev[idx].steps, [i]: 'done' } } }));
        } else {
          failedAt = i; failReason = result.message || 'please try again.';
          break;
        }
      } catch (e) {
        failedAt = i; failReason = "it couldn't be completed just now. Please try again in a moment.";
        break;
      }
    }

    if (failedAt >= 0) {
      // Mark the failed step, and every later not-yet-run step as skipped.
      setPlanState((prev) => {
        const nextSteps = { ...prev[idx].steps, [failedAt]: 'failed' };
        for (let j = failedAt + 1; j < steps.length; j += 1) {
          if (nextSteps[j] === 'pending' || nextSteps[j] === undefined) nextSteps[j] = 'skipped';
        }
        return { ...prev, [idx]: { ...prev[idx], phase: 'failed', steps: nextSteps, failReason } };
      });
      return;
    }

    // All client:false steps succeeded → surface each client:true step as its
    // own separate single-action confirm card (reuses the existing card + run path).
    setPlanState((prev) => ({ ...prev, [idx]: { ...prev[idx], phase: 'done' } }));
    const clientSteps = steps.filter((s) => s.client);
    if (clientSteps.length) {
      setMessages((prev) => [
        ...prev,
        ...clientSteps.map((s) => ({
          role: 'assistant',
          fromAction: true,
          content: s.detail || `One more to confirm: ${s.label}`,
          proposedAction: { type: s.type, label: s.label, params: withCarriedIds(s.params) },
        })),
      ]);
    }
  };

  // Lightweight markdown → HTML. The agent writes real markdown (### headings,
  // numbered lists, bullets, bold, italics); the old version only handled bold,
  // emitted orphan <li> with no <ul> parent, and glued every line with <br/> —
  // which is why replies read as one scattered block with no hierarchy.
  // Escapes first, so nothing the model emits can inject markup.
  const formatMessage = (content) => {
    const esc = (s) => String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const inline = (s) => esc(s)
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" data-nav="$2">$1</a>')
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-white">$1</strong>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em class="text-white/65">$2</em>')
      .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 rounded bg-black/40 border border-white/10 text-[#E7C96A] text-[12px]">$1</code>');
    const formatProse = (text) => {
      const out = [];
      let list = null;
      const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };
      for (const raw of String(text || '').split('\n')) {
        const line = raw.trimEnd();
        if (!line.trim()) { closeList(); out.push('<div class="h-2"></div>'); continue; }
        const h = line.match(/^\s*(#{1,4})\s+(.*)$/);
        if (h) {
          closeList();
          out.push(`<p class="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold mt-3 mb-1.5">${inline(h[2])}</p>`);
          continue;
        }
        if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
          closeList();
          out.push('<div class="my-2.5 border-t border-white/[0.08]"></div>');
          continue;
        }
        const ol = line.match(/^\s*\d+[.)]\s+(.*)$/);
        if (ol) {
          if (list !== 'ol') { closeList(); out.push('<ol class="list-decimal ml-5 space-y-1 my-1.5 marker:text-[#D4AF37]/70">'); list = 'ol'; }
          out.push(`<li class="pl-1">${inline(ol[1])}</li>`);
          continue;
        }
        const ul = line.match(/^\s*[-*•]\s+(.*)$/);
        if (ul) {
          if (list !== 'ul') { closeList(); out.push('<ul class="list-disc ml-5 space-y-1 my-1.5 marker:text-[#D4AF37]/60">'); list = 'ul'; }
          out.push(`<li class="pl-1">${inline(ul[1])}</li>`);
          continue;
        }
        closeList();
        out.push(`<p class="my-1">${inline(line)}</p>`);
      }
      closeList();
      return out.join('');
    };
    // Copy-ready deliverables the agent wrapped in [[COPY]] ... [[/COPY]] render
    // as their own card with a Copy button that copies ONLY that block as clean
    // plain text; the agent's own words stay outside the card.
    const src = String(content || '');
    const re = /\[\[COPY\]\]\s*([\s\S]*?)\s*\[\[\/COPY\]\]/g;
    const parts = [];
    let last = 0; let m;
    while ((m = re.exec(src)) !== null) {
      if (m.index > last) parts.push({ t: 'prose', v: src.slice(last, m.index) });
      parts.push({ t: 'copy', v: m[1] });
      last = re.lastIndex;
    }
    if (last < src.length) parts.push({ t: 'prose', v: src.slice(last) });
    if (!parts.length) parts.push({ t: 'prose', v: src });
    return parts.map((p) => {
      if (p.t === 'copy') {
        const clean = String(p.v || '').replace(/\n{3,}/g, '\n\n').trim();
        return '<div class="deliverable-card my-2 rounded-xl border border-[#D4AF37]/25 bg-black/20 overflow-hidden">'
          + '<div class="flex items-center justify-between px-3 py-1.5 border-b border-white/[0.06] bg-white/[0.02]">'
          + '<span class="text-[10px] uppercase tracking-[0.16em] text-[#D4AF37]/80 font-semibold">Ready to send</span>'
          + '<button type="button" data-copy="1" class="deliverable-copy text-[11px] font-semibold text-[#E6C86A] hover:text-white transition-colors px-2 py-0.5 rounded">Copy</button>'
          + '</div>'
          + `<pre class="deliverable-text whitespace-pre-wrap break-words px-3 py-2.5 text-[13px] leading-[1.6] text-white/90" style="font-family:inherit">${esc(clean)}</pre>`
          + '</div>';
      }
      return formatProse(p.v);
    }).join('');
  };

  // Don't render if not logged in
  const user = appStorage.getItem('user') || appSession.getItem('user');
  const token = appStorage.getItem('token') || appSession.getItem('token');
  if (!user && !token) return null;

  return (
    <>
      {/* Chat Bubble Button */}
      {!isOpen && (() => {
        const desktopPositioned = isDesktop && lPos;
        const isTucked = isDesktop && !!tucked;
        const innerRight = !isTucked || tucked === 'l';
        const badgePos = innerRight ? '-top-2 -right-2' : '-top-2 -left-2';
        const sizeClass = isTucked ? '' : 'w-11 h-11 rounded-[12px]';
        const cornerClass = desktopPositioned ? '' : 'bottom-6 right-6';
        const hoverClass = (!isTucked && !dragging) ? 'hover:scale-110' : '';
        const ariaLabel = isTucked
          ? 'Show STAFFED agent'
          : 'STAFFED agent. Press Enter to open. Drag or use arrow keys to move. Drag to a side edge to tuck away.';
        return (
        <button
          ref={launcherRef}
          onClick={onLauncherClick}
          onPointerDown={onLauncherPointerDown}
          onPointerMove={onLauncherPointerMove}
          onPointerUp={onLauncherPointerUp}
          onPointerCancel={onLauncherPointerUp}
          onKeyDown={onLauncherKeyDown}
          onMouseEnter={() => { if (!isTucked && !dragging) triggerGreet(); }}
          aria-label={ariaLabel}
          className={`fixed z-[10000] pointer-events-auto flex items-center justify-center overflow-visible p-0 shadow-[0_8px_24px_rgba(0,0,0,0.5)] group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0F1A] ${cornerClass} ${sizeClass} ${hoverClass} ${(totalUnseen > 0 || agentAttention || pkgReqCount > 0 || highValueMoveCount > 0) ? 'mtl-agent-glow' : ''}`}
          data-testid="ai-agent-bubble"
          data-staffed-agent="launcher"
          style={{
            background: isTucked ? 'linear-gradient(180deg, #D4AF37, #E5C76B)' : 'transparent',
            ...(desktopPositioned ? { left: lPos.left, top: lPos.top, right: 'auto', bottom: 'auto' } : {}),
            ...(isTucked ? { width: TAB_W, height: TAB_H, borderRadius: 8 } : {}),
            transition: (dragging || reducedMotion)
              ? 'none'
              : 'left 450ms cubic-bezier(0.22,1,0.36,1), top 450ms cubic-bezier(0.22,1,0.36,1), width 300ms ease, height 300ms ease',
            cursor: dragging ? 'grabbing' : (isDesktop ? 'grab' : 'pointer'),
            touchAction: 'none',
          }}
        >
          {isTucked ? (
            <span className="block w-[3px] h-6 rounded-full bg-[#0B0F1A]/50" />
          ) : (
            <SAgentOrb agentState={agentState} reducedMotion={reducedMotion} greetBubble={greetBubble} />
          )}
          {(waitingCount + pkgReqCount) > 0 && (
            <span
              className={`absolute ${badgePos} z-10 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold text-white tabular-nums border-2 border-[#0B0F1A]`}
              style={{ background: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.35)' }}
              data-testid="ai-agent-waiting-badge"
            >
              {waitingCount + pkgReqCount}
            </span>
          )}
          {/* Phase 4 Slice 3 — a high-value move is waiting and no client message
              is pending: a GOLD count badge so the opportunity is visible before
              S is opened. Gold ("a move to make") stays distinct from the red
              badge ("a client is waiting on you"). */}
          {highValueMoveCount > 0 && (waitingCount + pkgReqCount) === 0 && (
            <span
              className={`absolute ${badgePos} z-10 min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center text-[11px] font-bold text-[#0B0F1A] tabular-nums border-2 border-[#0B0F1A]`}
              style={{ background: 'linear-gradient(180deg, #F1D98A, #D4AF37)', boxShadow: '0 0 0 3px rgba(212,175,55,0.35)' }}
              data-testid="ai-agent-moves-badge"
            >
              {highValueMoveCount}
            </span>
          )}
          {/* S5-4: gold attention dot — the agent has a response/question for
              you and the panel is closed. Static (reduced-motion safe); the
              launcher pulse (mtl-agent-glow) carries the motion. */}
          {agentAttention && (waitingCount + pkgReqCount) === 0 && highValueMoveCount === 0 && (
            <span
              className={`absolute ${badgePos} z-10 w-3 h-3 rounded-full border-2 border-[#0B0F1A]`}
              style={{ background: '#E6C074', boxShadow: '0 0 0 3px rgba(230,192,116,0.35)' }}
              data-testid="ai-agent-attention-dot"
            />
          )}

          {/* Single hover prompt — "Need anything?" lives in SAgentOrb. */}
        </button>
        );
      })()}

      {/* Drag-to-tuck affordances */}
      {!isOpen && isDesktop && dragging && tuckHint && (
        <>
          <div
            className="fixed top-0 bottom-0 z-[9998] pointer-events-none"
            style={{
              [tuckHint === 'l' ? 'left' : 'right']: 0,
              width: 80,
              background: tuckHint === 'l'
                ? 'linear-gradient(90deg, rgba(212,175,55,0.35), transparent)'
                : 'linear-gradient(270deg, rgba(212,175,55,0.35), transparent)',
            }}
          />
          <div
            className="fixed z-[10000] pointer-events-none px-3 py-1.5 rounded-full text-xs font-semibold text-[#0B0F1A]"
            style={{
              top: lPos ? Math.max(L_MARGIN, lPos.top - 8) : 100,
              [tuckHint === 'l' ? 'left' : 'right']: 24,
              background: '#D4AF37',
              boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          >
            Release to tuck away
          </div>
        </>
      )}

      {/* Chat Window */}
      {isOpen && (() => {
        const anchored = isDesktop && lPos && (tucked || !launcherAtDefault());
        const vpH = typeof window !== 'undefined' ? window.innerHeight : 800;
        // S39c — grow the panel while client messages are waiting (desktop
        // only; phone stays full-screen). Never below the current 500px.
        const grownH = (isDesktop && !isMinimized && waitingCount > 0)
          ? Math.max(500, Math.min(720, vpH - 48))
          : null;
        const winStyle = anchored ? computeWindowPos(isMinimized, grownH) : undefined;
        const canResize = isDesktop && !isMinimized;
        // User size wins over both the default and the client-message auto-grow.
        let outerStyle = anchored ? { left: winStyle.left, top: winStyle.top, right: 'auto', bottom: 'auto', width: winStyle.width } : undefined;
        let innerStyle = grownH ? { height: grownH } : undefined;
        if (canResize && resizeBox) {
          outerStyle = { left: resizeBox.left, top: resizeBox.top, right: 'auto', bottom: 'auto', width: resizeBox.width };
          innerStyle = { height: resizeBox.height };
        } else if (canResize && (panelPos || panelSize)) {
          const w = panelSize ? panelSize.w : 384;
          const h = panelSize ? panelSize.h : (grownH || 500);
          if (panelPos) {
            // Clamp the remembered position into the current viewport.
            const left = Math.max(16, Math.min(panelPos.left, window.innerWidth - w - 16));
            const top = Math.max(16, Math.min(panelPos.top, window.innerHeight - h - 16));
            outerStyle = { left, top, right: 'auto', bottom: 'auto', width: w };
          } else {
            outerStyle = { ...(outerStyle || {}), width: w };
          }
          innerStyle = { height: h };
        }
        // S39c — pinned region cap = window − header − input row − a
        // guaranteed 72px chat-log peek. The chat log (flex-1) takes the rest.
        const HEADER_H = 73, INPUT_ROW_H = 78, CHAT_PEEK = 72;
        const panelHForCap = isDesktop ? (grownH || 500) : vpH;
        const pinnedCap = Math.max(120, panelHForCap - HEADER_H - INPUT_ROW_H - CHAT_PEEK);
        return (
        <div 
          className={`fixed z-[10000] pointer-events-auto ${resizing ? '' : 'transition-all duration-300'} ${
            isMinimized 
              ? 'bottom-6 right-6 w-72' 
              : 'inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-96'
          }`}
          style={outerStyle}
          data-testid="ai-agent-window"
          data-staffed-agent="panel"
        >
          <div className={`bg-[#0B0F1A]/95 backdrop-blur-xl border border-[#D4AF37]/30 shadow-2xl shadow-black/50 overflow-hidden flex flex-col ${
            isMinimized ? 'rounded-2xl h-auto' : 'h-full sm:h-[500px] sm:rounded-2xl'
          }`}
            style={innerStyle}
          >
            
            {/* Header — drag anywhere in the empty header space to move the panel. */}
            <div
              onPointerDown={startPanelMove}
              className={`p-4 bg-gradient-to-r from-[#D4AF37]/20 to-[#E5C76B]/10 border-b border-[#D4AF37]/20 flex items-center justify-between ${canResize ? 'sm:cursor-move' : ''}`}
              data-testid="ai-agent-header"
            >
              <div className="flex items-center gap-3">
                <SAgentBadge className="w-10 h-10 shadow-lg shadow-[#D4AF37]/20" sSize={20} />
                <div>
                  <h3 className="font-bold text-white text-sm">STAFFED Agent</h3>
                  <p className="text-[#D4AF37] text-xs">Your Event Assistant</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                {!isMinimized && messages.length > 0 && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={clearHistory}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Clear Chat</TooltipContent>
                  </Tooltip>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    >
                      <Minimize2 className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{isMinimized ? "Expand" : "Minimize"}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Close</TooltipContent>
                </Tooltip>
              </div>
            </div>

            {!isMinimized && (
              <>
                {/* S38 — PINNED waiting list, never scrolls with the chat log */}
                {waitingCount > 0 && (
                  <div
                    className="shrink-0 border-b border-white/10 bg-[#0B0F1A]/60"
                    data-testid="ai-agent-waiting-list"
                  >
                    <button
                      type="button"
                      onClick={toggleWaiting}
                      aria-expanded={waitingOpen}
                      className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left hover:bg-white/[0.02] transition-colors"
                      data-testid="ai-agent-waiting-toggle"
                    >
                      <span className="w-[22px] h-[22px] rounded-[7px] shrink-0 flex items-center justify-center bg-[#D4AF37]/[0.12] border border-[#D4AF37]/30 text-[#F1D98A]">
                        <svg viewBox="0 0 24 24" fill="none" className="w-[13px] h-[13px]"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/></svg>
                      </span>
                      <span className="flex-1 text-[12.5px] font-bold text-white">{waitingCount} client {waitingCount === 1 ? 'message' : 'messages'} waiting</span>
                      <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full text-[#F1D98A] bg-[#D4AF37]/[0.12] border border-[#D4AF37]/30">{waitingCount}</span>
                      <svg viewBox="0 0 24 24" fill="none" className="w-[15px] h-[15px] text-white/40" style={{ transform: waitingOpen ? 'rotate(180deg)' : 'none', transition: 'transform .28s cubic-bezier(0.16,1,0.3,1)' }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {waitingOpen && (
                    <div className="px-3 pb-3 pt-1 space-y-2 overflow-y-auto border-t border-white/[0.06]" style={{ maxHeight: pinnedCap }}>
                    {waitingEvents.map((ev, idx) => {
                      const mins = Math.max(0, Math.round((ev.waitingHours || 0) * 60));
                      const waitLabel = mins >= 60 ? `waiting ${Math.round(mins / 60)}h` : `waiting ${mins} min`;
                      const dateStr = ev.eventDate ? formatDate(ev.eventDate) : '';
                      const expanded = expandedId ? expandedId === ev.eventId : idx === 0;
                      if (!expanded) {
                        return (
                          <button
                            key={ev.eventId}
                            type="button"
                            onClick={() => setExpandedId(ev.eventId)}
                            className="w-full text-left rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 px-3 py-2 transition flex items-center justify-between gap-2"
                            data-testid={`ai-agent-waiting-${ev.eventId}`}
                          >
                            <span className="text-[12px] font-semibold text-white truncate">{ev.clientName}</span>
                            <span className="text-[11px] text-[#E7C96A] font-semibold shrink-0">{waitLabel}</span>
                          </button>
                        );
                      }
                      return (
                        <div
                          key={ev.eventId}
                          className="rounded-lg bg-white/[0.03] border border-white/10 px-3 py-3 space-y-2"
                          data-testid={`ai-agent-waiting-${ev.eventId}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-[13px] font-bold text-white truncate">{ev.clientName}</p>
                            <p className="text-[11px] text-[#E7C96A] font-semibold shrink-0 text-right">{waitLabel}</p>
                          </div>
                          <p className="text-[11px] text-white/45">{ev.eventName}{dateStr ? ` · ${dateStr}` : ''}</p>
                          {ev.preview && (
                            <p
                              className="text-[12px] text-white/80 pl-2 overflow-hidden"
                              style={{ borderLeft: '2px solid #D4AF37', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
                            >
                              {ev.preview}
                            </p>
                          )}
                          {/* S39: suggested reply */}
                          {renderSuggestedReply(ev)}
                        </div>
                      );
                    })}
                    </div>
                    )}
                  </div>
                )}
                {/* Phase 4 Slice 2 — "What I'd do next" (shown when no client messages are waiting). */}
                {waitingCount === 0 && proactiveMoves.length > 0 && (
                  <div
                    className="shrink-0 border-b border-white/10 bg-[#0B0F1A]/60"
                    data-testid="ai-agent-next-moves"
                  >
                    <button
                      type="button"
                      onClick={toggleMoves}
                      aria-expanded={movesOpen}
                      className="flex items-center gap-2.5 w-full px-3.5 py-2.5 text-left hover:bg-white/[0.02] transition-colors"
                      data-testid="ai-agent-moves-toggle"
                    >
                      <span className="w-[22px] h-[22px] rounded-[7px] shrink-0 flex items-center justify-center bg-[#D4AF37]/[0.12] border border-[#D4AF37]/30 text-[#F1D98A]">
                        <svg viewBox="0 0 24 24" fill="none" className="w-[13px] h-[13px]"><path d="M13 2L4.5 13.5H11l-1 8.5L19.5 10H13l0-8z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>
                      </span>
                      <span className="flex-1 text-[13px] font-bold text-white">What I'd do next</span>
                      <span className="text-[10px] font-extrabold px-2.5 py-1 rounded-full text-[#F1D98A] bg-[#D4AF37]/[0.12] border border-[#D4AF37]/30">
                        {proactiveMoves.length} {proactiveMoves.length === 1 ? 'move' : 'moves'}
                      </span>
                      <svg viewBox="0 0 24 24" fill="none" className="w-[15px] h-[15px] text-white/40" style={{ transform: movesOpen ? 'rotate(180deg)' : 'none', transition: 'transform .28s cubic-bezier(0.16,1,0.3,1)' }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </button>
                    {movesOpen && (
                    <div className="px-3 pb-3 overflow-y-auto border-t border-white/[0.06]" style={{ maxHeight: pinnedCap }}>
                      <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#141A2A] overflow-hidden mt-2">
                        <div className="px-4 pb-2.5 pt-3 text-[10.5px] leading-[1.5] text-white/45">
                        Ranked by what's most urgent and worth the most. One tap handles each — I draft or stage it, you confirm.
                      </div>
                      {proactiveMoves.map((mv, mi) => {
                        const toneCls = mv.impactTone === 'red'
                          ? 'text-[#F87171] bg-[#F87171]/[0.12] border-[#F87171]/30'
                          : 'text-[#F5B301] bg-[#F5B301]/[0.12] border-[#F5B301]/30';
                        const icon = mv.tag === 'payment' ? (
                          <svg viewBox="0 0 24 24" fill="none" className="w-[17px] h-[17px]"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7"/><path d="M12 7v10M9.5 9.2c0-1.1 1.1-1.8 2.5-1.8s2.5.7 2.5 1.8-1.1 1.6-2.5 1.6-2.5.6-2.5 1.7 1.1 1.8 2.5 1.8 2.5-.7 2.5-1.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        ) : mv.tag === 'staffing' ? (
                          <svg viewBox="0 0 24 24" fill="none" className="w-[17px] h-[17px]"><circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7"/><path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/><path d="M17 8.5a2.6 2.6 0 010 5M19 19c0-2.2-1-3.8-2.5-4.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                        ) : mv.tag === 'lead' ? (
                          <svg viewBox="0 0 24 24" fill="none" className="w-[17px] h-[17px]"><path d="M12 3c2.5 3 4 5 4 7.5A4 4 0 018 10.5C8 8 9.5 6 12 3z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M7 15.5c0 3 2.2 5 5 5s5-2 5-5c0-1.4-.6-2.6-1.5-3.6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" className="w-[17px] h-[17px]"><path d="M6 3h8l4 4v14H6z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M14 3v4h4M9 12h6M9 16h6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                        );
                        let primaryUsed = false;
                        return (
                          <div key={mv.id} className={`px-4 py-3 border-t border-white/[0.05] ${mi === 0 ? 'bg-gradient-to-b from-[#D4AF37]/[0.06] to-transparent' : ''}`}>
                            <div className="flex items-start gap-3">
                              <div className="w-8 h-8 rounded-[9px] shrink-0 flex items-center justify-center bg-white/[0.045] text-[#F1D98A]">{icon}</div>
                              <div className="flex-1 min-w-0">
                                {mi === 0 && <div className="text-[9px] font-extrabold tracking-[0.1em] uppercase text-[#F1D98A] mb-0.5">Do first</div>}
                                <div className="text-[13.5px] font-bold text-white leading-tight">{mv.title}</div>
                                {mv.reason && <div className="text-[11.5px] text-white/45 mt-0.5 leading-snug">{mv.reason}</div>}
                              </div>
                              {mv.impact && <span className={`shrink-0 text-[10px] font-bold px-2 py-1 rounded-full whitespace-nowrap border ${toneCls}`}>{mv.impact}</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-2.5 ml-11">
                              {(mv.actions || []).map((act, ai) => {
                                if (act.kind === 'open') {
                                  return (
                                    <button key={ai} type="button" onClick={() => runMoveAction(mv, act)} className="text-[11px] text-white/35 hover:text-white/70 transition">{act.label}</button>
                                  );
                                }
                                const isPrimary = !primaryUsed; primaryUsed = true;
                                return (
                                  <button key={ai} type="button" onClick={() => runMoveAction(mv, act)}
                                    className={isPrimary
                                      ? 'text-[11.5px] font-bold px-3.5 py-[7px] rounded-[9px] bg-gradient-to-br from-[#F1D98A] to-[#D4AF37] text-[#0B0F1A]'
                                      : 'text-[11.5px] font-semibold px-3 py-[7px] rounded-[9px] border border-white/15 text-white/80 hover:bg-white/5 transition'}>
                                    {act.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                      <div className="px-4 py-3 border-t border-white/[0.06]">
                        <p className="text-[10.5px] text-white/45 leading-[1.5]">I keep watching — this updates as things change.</p>
                      </div>
                    </div>
                    </div>
                    )}
                  </div>
                )}
                {/* Messages Area */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ minHeight: CHAT_PEEK }}>
                  {messages.length === 0 && (
                    <div className="text-center py-8">
                      <h4 className="text-white font-semibold mb-2">How can I help?</h4>
                      <p className="text-gray-400 text-sm mb-4">
                        Ask me anything about managing your staff, events, or the STAFFED platform.
                      </p>
                      <div className="space-y-2">
                        {[
                          "Send availability requests to my dancers",
                          "Show me staff performance rankings",
                          "Who should I assign to my next event?",
                          "Help me plan an event"
                        ].map((suggestion, idx) => (
                          <button
                            key={idx}
                            onClick={() => setInputValue(suggestion)}
                            className="block w-full text-left px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4AF37]/30 rounded-xl text-sm text-gray-300 hover:text-white transition-all"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {messages.map((msg, idx) => {
                    const isUser = msg.role === 'user';
                    const canRate = !isUser && idx > 0 && messages[idx - 1]?.role === 'user';
                    const rated = feedback[idx];
                    return (
                    <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                      <div className={`flex flex-col ${isUser ? 'max-w-[85%]' : 'w-full max-w-full'}`}>
                        <div
                          className={`px-4 py-3 rounded-2xl ${isUser
                            ? 'bg-gradient-to-br from-[#E6C86A] to-[#C5A028] text-[#0B0F1A] shadow-[0_8px_24px_-8px_rgba(212,175,55,0.5)]'
                            : 'bg-[#0B1224]/70 text-white/85 border border-white/[0.07] backdrop-blur-xl shadow-[0_16px_50px_-24px_rgba(0,0,0,0.85)]'}`}
                        >
                          {/* whitespace-pre-wrap removed on purpose — the formatter now
                              emits real block elements, so pre-wrap doubled every gap. */}
                          <div
                            className="text-[13px] leading-[1.65] tracking-[-0.005em] [&_strong]:text-white [&_a]:text-[#E7C96A] [&_a]:underline [&_a]:cursor-pointer"
                            onClick={(e) => {
                              const copyBtn = e.target.closest('button[data-copy]');
                              if (copyBtn) {
                                e.preventDefault();
                                const card = copyBtn.closest('.deliverable-card');
                                const pre = card && card.querySelector('.deliverable-text');
                                const txt = pre ? pre.textContent : '';
                                const done = () => { copyBtn.textContent = 'Copied ✓'; setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1500); };
                                try { navigator.clipboard.writeText(txt).then(done, done); }
                                catch { const ta = document.createElement('textarea'); ta.value = txt; ta.style.position='fixed'; ta.style.opacity='0'; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch (_) {} document.body.removeChild(ta); done(); }
                                return;
                              }
                              const a = e.target.closest('a[data-nav]');
                              if (a) {
                                const to = a.getAttribute('data-nav');
                                if (to && to.startsWith('/')) { e.preventDefault(); setIsOpen(false); navigate(to); }
                              }
                            }}
                            dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                          />
                        </div>
                        {/* BLOCK S7 1.b — when the answer matches a guide, offer Start guide.
                            Suppressed for action-result confirmations (they carry their own
                            deep-link, e.g. Open Staffing, and shouldn't surface an unrelated guide). */}
                        {(() => {
                          if (isUser || msg.fromAction) return null;
                          const g = matchGuide(msg.content);
                          if (!g) return null;
                          const m = window.location.pathname.match(/^\/event\/([^/]+)/);
                          return (
                            <button
                              type="button"
                              data-testid={`assistant-start-guide-${g.id}`}
                              onClick={() => startGuide(g.id, m ? m[1] : null)}
                              className="mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold text-[#0B0F1A] w-fit"
                              style={{ background: 'linear-gradient(180deg,#E7C75E,#D4AF37)' }}
                            >
                              <ArrowRight className="w-3.5 h-3.5" /> Start guide: {g.shortTitle}
                            </button>
                          );
                        })()}
                        {!isUser && (
                          <div className="flex items-center gap-2 mt-1.5 pl-1">
                            <button type="button" onClick={() => copyMessage(idx, msg.content)} className="text-white/25 hover:text-[#E6C86A] transition-colors" aria-label="Copy" title={copied[idx] ? 'Copied' : 'Copy'}>
                              {copied[idx] ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                            </button>
                            {canRate && (rated ? (
                              <span className="text-[11px] text-white/40">Thanks for the feedback</span>
                            ) : (
                              <>
                                <button type="button" onClick={() => sendFeedback(idx, 'up')} className="text-white/25 hover:text-emerald-400 transition-colors" aria-label="Helpful" title="Helpful"><ThumbsUp className="w-3.5 h-3.5" /></button>
                                <button type="button" onClick={() => sendFeedback(idx, 'down')} className="text-white/25 hover:text-red-400 transition-colors" aria-label="Not helpful" title="Not helpful"><ThumbsDown className="w-3.5 h-3.5" /></button>
                              </>
                            ))}
                          </div>
                        )}
                        {!isUser && Array.isArray(msg.standingJobs) && msg.standingJobs.length > 0 && (() => {
                          const sjobs = msg.standingJobs.filter((j) => !(autopilotCtl[j.jobId]?.removed));
                          if (sjobs.length === 0) return null;
                          const running = sjobs.filter((j) => (autopilotCtl[j.jobId]?.runState || j.runState) === 'running');
                          return (
                            <div className="mt-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3" data-testid={`autopilot-card-${idx}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                                <p className="text-[13px] text-white/90 font-semibold flex-1">On autopilot</p>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#F1D98A] border border-[#D4AF37]/30" data-testid={`autopilot-count-${idx}`}>{running.length} running</span>
                              </div>
                              <div className="space-y-2">
                                {sjobs.map((job, i) => {
                                  const rs = autopilotCtl[job.jobId]?.runState || job.runState;
                                  const open = autopilotCtl[job.jobId]?.open;
                                  const Icon = job.jobType === 'payment_chase' ? DollarSign : (job.jobType === 'staffing_keep_staffed' ? Users : Zap);
                                  return (
                                    <div key={job.jobId} className="rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid={`autopilot-row-${idx}-${i}`}>
                                      <div className="flex items-start gap-2.5">
                                        <div className="w-7 h-7 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center flex-shrink-0">
                                          <Icon className="w-3.5 h-3.5 text-[#F1D98A]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[12.5px] text-white/90 font-medium leading-snug">{job.goal}</p>
                                          <p className="text-[11px] text-white/45 mt-0.5">{job.cadenceText}</p>
                                        </div>
                                        {rs === 'paused' ? (
                                          <span className="text-[9.5px] font-semibold px-2 py-0.5 rounded-full border bg-amber-500/15 text-amber-400 border-amber-500/30 flex-shrink-0">Paused</span>
                                        ) : (
                                          <span className="text-[9.5px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-400 border-emerald-500/30 flex-shrink-0 inline-flex items-center gap-1.5">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Running
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-[11.5px] text-white/70 mt-2 ml-[38px]"><span className="text-[#F1D98A] font-medium">{job.statusText}</span></p>
                                      {job.lastActivity ? <p className="text-[10.5px] text-white/35 mt-1 ml-[38px]">{job.lastActivity}</p> : null}
                                      {job.clientFacing ? <p className="text-[10.5px] text-amber-400/90 mt-1 ml-[38px]">Touches the client — you approved this cadence.</p> : null}
                                      <div className="flex items-center gap-2 mt-2 ml-[38px]">
                                        {job.mode === 'manual' && job.sendDue && !autopilotCtl[job.jobId]?.sentNow && (
                                          <button type="button" onClick={() => sendReminderNow(job.jobId)} className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] hover:opacity-90 transition-opacity">Send reminder now</button>
                                        )}
                                        {rs === 'paused' ? (
                                          <button type="button" onClick={() => controlStandingJob(job.jobId, 'resume')} className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-white/15 text-white/70 hover:text-white/90 transition-colors">Resume</button>
                                        ) : (
                                          <button type="button" onClick={() => controlStandingJob(job.jobId, 'pause')} className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-white/15 text-white/70 hover:text-white/90 transition-colors">Pause</button>
                                        )}
                                        <button type="button" onClick={() => controlStandingJob(job.jobId, 'stop')} className="text-[11px] font-medium px-2.5 py-1 rounded-md border border-red-500/35 text-red-400 hover:text-red-300 transition-colors">Stop</button>
                                        {Array.isArray(job.activity) && job.activity.length > 0 && (
                                          <button type="button" onClick={() => toggleJobActivity(job.jobId)} className="text-[11px] font-medium px-2.5 py-1 rounded-md text-white/40 hover:text-white/70 transition-colors">{open ? 'Hide' : 'Activity'}</button>
                                        )}
                                      </div>
                                      {open && Array.isArray(job.activity) && job.activity.length > 0 && (
                                        <div className="mt-2 ml-[38px] rounded-md border border-white/10 bg-black/30 p-2 space-y-1">
                                          {job.activity.slice().reverse().map((a, ai) => (
                                            <p key={ai} className="text-[10.5px] text-white/50">{a.text}</p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-white/[0.06]">
                                <Clock className="w-3 h-3 text-white/30 flex-shrink-0" />
                                <p className="text-[10.5px] text-white/40">These run on their own until the goal lands or you stop them.</p>
                              </div>
                            </div>
                          );
                        })()}
                        {!isUser && Array.isArray(msg.startedJobs) && msg.startedJobs.length > 0 && (() => {
                          const jobs = msg.startedJobs;
                          const openJobs = jobs.filter((j) => j.state === 'waiting' || j.state === 'needs_you');
                          const doneJobs = jobs.filter((j) => j.state === 'done');
                          const ICONS = { staffing: Users, proposal: FileText, payment: DollarSign, call: Phone, availability: Calendar };
                          const CHIP = {
                            waiting: { cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30', label: 'Waiting' },
                            needs_you: { cls: 'bg-red-500/15 text-red-400 border-red-500/30', label: 'Needs you' },
                            done: { cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30', label: 'Done' },
                          };
                          const Row = (job, i) => {
                            const Icon = ICONS[job.watchTag] || Zap;
                            const chip = CHIP[job.state] || CHIP.waiting;
                            const tappable = !!job.eventId;
                            return (
                              <div
                                key={i}
                                role={tappable ? 'button' : undefined}
                                tabIndex={tappable ? 0 : undefined}
                                onClick={tappable ? () => { setIsOpen(false); navigate(`/event/${job.eventId}`); } : undefined}
                                className={`flex items-center gap-2.5 py-2 px-2 rounded-lg transition-colors ${tappable ? 'cursor-pointer hover:bg-white/[0.04]' : ''}`}
                                data-testid={`tracker-row-${idx}-${i}`}
                              >
                                <div className="w-7 h-7 rounded-lg bg-white/[0.05] border border-white/10 flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-3.5 h-3.5 text-[#F1D98A]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12.5px] text-white/90 font-medium truncate">{job.label}</p>
                                  <p className="text-[11px] text-white/45 truncate">{job.status}</p>
                                </div>
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${chip.cls}`}>{chip.label}</span>
                              </div>
                            );
                          };
                          return (
                            <div className="mt-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3" data-testid={`tracker-card-${idx}`}>
                              <div className="flex items-center gap-2 mb-2">
                                <Zap className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                                <p className="text-[13px] text-white/90 font-semibold flex-1">What I've started</p>
                                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#D4AF37]/15 text-[#F1D98A] border border-[#D4AF37]/30" data-testid={`tracker-open-count-${idx}`}>{openJobs.length} open</span>
                              </div>
                              {openJobs.length > 0 ? (
                                <div className="space-y-0.5">{openJobs.map((j, i) => Row(j, i))}</div>
                              ) : (
                                <p className="text-[11.5px] text-white/40 px-2 py-1.5">Nothing open right now.</p>
                              )}
                              {doneJobs.length > 0 && (
                                <>
                                  <div className="flex items-center gap-1.5 mt-2.5 mb-1 px-2">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-400/70" />
                                    <p className="text-[10px] uppercase tracking-wide text-white/35 font-semibold">Done</p>
                                  </div>
                                  <div className="space-y-0.5 opacity-75">{doneJobs.map((j, i) => Row(j, `done-${i}`))}</div>
                                </>
                              )}
                              <div className="flex items-center gap-1.5 mt-2.5 pt-2 border-t border-white/[0.06]">
                                <Clock className="w-3 h-3 text-white/30 flex-shrink-0" />
                                <p className="text-[10.5px] text-white/40">Live status — I keep watching these for you. Tap one to open its event.</p>
                              </div>
                            </div>
                          );
                        })()}

                        {!isUser && msg.proposedPlan && planState[idx]?.phase !== 'dismissed' && (() => {
                          const plan = msg.proposedPlan;
                          const ps = planState[idx] || { phase: 'proposed', steps: {} };
                          const phase = ps.phase || 'proposed';
                          const stepStatus = (i) => ps.steps?.[i] || 'pending';
                          const clientCount = (plan.steps || []).filter((s) => s.client).length;
                          return (
                            <div className="mt-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3" data-testid={`plan-card-${idx}`}>
                              <div className="flex items-start gap-2">
                                <Zap className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-[13px] text-white/90 font-semibold leading-snug">{plan.title}</p>
                                  <p className="text-[11px] text-white/40 mt-0.5">Nothing runs until you tap Confirm plan.</p>

                                  <div className="mt-2.5 space-y-1.5">
                                    {(plan.steps || []).map((step, i) => {
                                      const st = stepStatus(i);
                                      return (
                                        <div key={i} className="flex items-start gap-2.5" data-testid={`plan-step-${idx}-${i}`}>
                                          <div className="mt-0.5 w-5 h-5 flex items-center justify-center flex-shrink-0">
                                            {st === 'done' ? (
                                              <Check className="w-4 h-4 text-emerald-400" />
                                            ) : st === 'failed' ? (
                                              <AlertCircle className="w-4 h-4 text-amber-400" />
                                            ) : st === 'running' ? (
                                              <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" />
                                            ) : st === 'skipped' ? (
                                              <span className="w-5 h-5 rounded-full border border-white/15 flex items-center justify-center text-[10px] text-white/30">{i + 1}</span>
                                            ) : (
                                              <span className="w-5 h-5 rounded-full border border-white/25 flex items-center justify-center text-[10px] text-white/60">{i + 1}</span>
                                            )}
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className={`text-[12.5px] leading-snug ${st === 'skipped' ? 'text-white/35' : 'text-white/90'} ${st === 'done' ? 'font-medium' : ''}`}>{step.label}</span>
                                              {step.client && (
                                                <span className="text-[9.5px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#F1D98A]/15 text-[#F1D98A] border border-[#F1D98A]/25" data-testid={`plan-step-client-pill-${idx}-${i}`}>Client send</span>
                                              )}
                                            </div>
                                            {step.detail && st !== 'skipped' && (
                                              <p className="text-[11px] text-white/45 mt-0.5 leading-snug">{step.detail}</p>
                                            )}
                                            {st === 'skipped' && (
                                              <p className="text-[11px] text-white/35 mt-0.5 italic">Skipped — stopped above</p>
                                            )}
                                            {st === 'failed' && ps.failReason && (
                                              <p className="text-[11px] text-amber-400/90 mt-0.5 leading-snug">I couldn't complete that: {ps.failReason}</p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {phase === 'done' && (
                                    <div className="mt-2.5 flex items-center gap-1.5 text-[12px] text-emerald-400" data-testid={`plan-success-${idx}`}>
                                      <Check className="w-3.5 h-3.5" />
                                      {clientCount > 0
                                        ? `All set — confirm the ${clientCount} client send${clientCount === 1 ? '' : 's'} below.`
                                        : 'All steps done.'}
                                    </div>
                                  )}
                                  {phase === 'failed' && (
                                    <div className="mt-2.5 flex items-center gap-1.5 text-[12px] text-amber-400" data-testid={`plan-failed-${idx}`}>
                                      <AlertCircle className="w-3.5 h-3.5" /> Stopped — one step couldn't complete.
                                    </div>
                                  )}

                                  <p className="text-[11px] text-white/40 mt-2.5">Client sends are confirmed one at a time, never as part of the plan.</p>

                                  {phase === 'proposed' && (
                                    <div className="flex items-center gap-2 mt-2.5">
                                      <button
                                        type="button"
                                        onClick={() => executePlan(idx, plan)}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] text-[12px] font-semibold hover:opacity-90 transition-opacity"
                                        data-testid={`plan-confirm-${idx}`}
                                      >
                                        <Check className="w-3.5 h-3.5" /> Confirm plan
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => dismissPlan(idx)}
                                        className="px-3 py-1.5 rounded-lg text-[12px] text-white/50 hover:text-white/80 transition-colors"
                                        data-testid={`plan-dismiss-${idx}`}
                                      >
                                        Not now
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {!isUser && msg.proposedAction && actionStatus[idx] !== 'dismissed' && (
                          <div className="mt-2 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] p-3">
                            <div className="flex items-start gap-2">
                              <Zap className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-[13px] text-white/90 font-medium leading-snug">
                                  {msg.proposedAction.label}
                                </p>
                                <p className="text-[11px] text-white/40 mt-0.5">
                                  Nothing sends until you tap Confirm.
                                </p>
                                {/* Show EXACTLY what will be sent — never confirm blind. */}
                                {msg.proposedAction.params && (msg.proposedAction.params.subject || msg.proposedAction.params.message) && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5 max-h-52 overflow-y-auto">
                                    {Array.isArray(msg.proposedAction.params.staff_ids) && (
                                      <p className="text-[11px] font-semibold text-[#E7C96A]">
                                        To: {msg.proposedAction.params.staff_ids.length} staff member{msg.proposedAction.params.staff_ids.length === 1 ? '' : 's'}
                                      </p>
                                    )}
                                    {msg.proposedAction.params.subject && (
                                      <p className="text-[12px] text-white/85 font-medium mt-1">
                                        Subject: {msg.proposedAction.params.subject}
                                      </p>
                                    )}
                                    {msg.proposedAction.params.message && (
                                      <p className="text-[12px] text-white/55 mt-1.5 whitespace-pre-wrap break-words">
                                        {msg.proposedAction.params.message}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {/* Staff-an-event plan — role → staffer, call time, pay, plus gaps. */}
                                {msg.proposedAction.type === 'staff_event' && Array.isArray(msg.proposedAction.params?.assignments) && msg.proposedAction.params.assignments.length > 0 && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5 max-h-52 overflow-y-auto" data-testid="staff-event-plan-preview">
                                    <p className="text-[11px] font-semibold text-[#E7C96A]">
                                      Proposed staffing — {msg.proposedAction.params.assignments.length} assignment{msg.proposedAction.params.assignments.length === 1 ? '' : 's'}
                                    </p>
                                    {msg.proposedAction.params.assignments.map((a, i) => (
                                      <p key={i} className="text-[12px] text-white/85 mt-1">
                                        <span className="text-white/45">{a.role || 'Staff'}:</span> {a.staffName || a.staffId}
                                        {a.callTime ? <span className="text-white/45"> · call {to12h(a.callTime)}</span> : null}
                                        {(a.payRate !== undefined && a.payRate !== null) ? <span className="text-white/45"> · ${a.payRate}</span> : null}
                                      </p>
                                    ))}
                                    {Array.isArray(msg.proposedAction.params.gaps) && msg.proposedAction.params.gaps.length > 0 && (
                                      <p className="text-[11px] text-amber-300/90 mt-2">
                                        Gaps: {msg.proposedAction.params.gaps.map((g) => (typeof g === 'object' ? `${g.role || 'role'} (${g.open ?? '?'} open)` : String(g))).join(', ')}
                                      </p>
                                    )}
                                  </div>
                                )}
                                {msg.proposedAction.type === 'record_payment' && msg.proposedAction.params && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid="record-payment-preview">
                                    <p className="text-[11px] font-semibold text-[#E7C96A]">Payment to record</p>
                                    {(msg.proposedAction.params.amount !== undefined && msg.proposedAction.params.amount !== null && msg.proposedAction.params.amount !== '') && (
                                      <p className="text-[12px] text-white/85 mt-1">
                                        <span className="text-white/45">Amount:</span> ${Number(msg.proposedAction.params.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </p>
                                    )}
                                    {msg.proposedAction.params.method && (
                                      <p className="text-[12px] text-white/85 mt-1">
                                        <span className="text-white/45">Method:</span> {String(msg.proposedAction.params.method).replace('_', ' ')}
                                      </p>
                                    )}
                                    <p className="text-[12px] text-white/85 mt-1">
                                      <span className="text-white/45">Date:</span> {msg.proposedAction.params.paidAt || 'Today'}
                                    </p>
                                    <p className="text-[12px] text-white/85 mt-1">
                                      <span className="text-white/45">Receipt to client:</span> {msg.proposedAction.params.sendReceipt === false ? 'No' : 'Yes'}
                                    </p>
                                  </div>
                                )}
                                {msg.proposedAction.type === 'complete_event_details' && msg.proposedAction.params && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5" data-testid="complete-event-details-preview">
                                    <p className="text-[11px] font-semibold text-[#E7C96A]">Details to fill</p>
                                    {[
                                      ['venue', 'Venue'],
                                      ['venue_address', 'Venue address'],
                                      ['start_time', 'Start time'],
                                      ['end_time', 'End time'],
                                      ['guest_count', 'Guest count'],
                                      ['client_email', 'Client email'],
                                      ['client_phone', 'Client phone'],
                                      ['load_in_time', 'Load-in time'],
                                      ['notes', 'Notes'],
                                    ].filter(([k]) => {
                                      const v = msg.proposedAction.params[k];
                                      return v !== undefined && v !== null && String(v).trim() !== '';
                                    }).map(([k, label]) => (
                                      <p key={k} className="text-[12px] text-white/85 mt-1">
                                        <span className="text-white/45">{label}:</span> {String(msg.proposedAction.params[k])}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                {msg.proposedAction.type === 'start_standing_job' && msg.proposedAction.params && msg.proposedAction.params.jobType === 'payment_chase' && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5 space-y-1.5" data-testid="start-chase-preview">
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">Goal:</span> {msg.proposedAction.params.goal || 'Get this balance paid'}</p>
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">The sends:</span> {msg.proposedAction.params.cadenceText || 'A reminder to the client every 3 days — up to 3, then it waits for you'}</p>
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">Stops when:</span> the balance is paid, or you stop it</p>
                                    <div className="mt-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.10] p-2.5">
                                      <p className="text-[11.5px] text-amber-200/90 leading-relaxed"><span className="font-semibold text-white">You're approving all 3 client reminders now</span>, on this schedule. I'll show each one in the tracker as it goes, and you can stop the job after any of them.</p>
                                    </div>
                                  </div>
                                )}
                                {msg.proposedAction.type === 'start_standing_job' && msg.proposedAction.params && msg.proposedAction.params.jobType !== 'payment_chase' && (
                                  <div className="mt-2 rounded-lg border border-white/10 bg-black/30 p-2.5 space-y-1.5" data-testid="start-standing-job-preview">
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">Goal:</span> {msg.proposedAction.params.goal || 'Keep this event fully staffed'}</p>
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">How often:</span> {msg.proposedAction.params.cadenceText || 'Checking daily · nudges unconfirmed crew at 48h & 24h'}</p>
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">Who I contact:</span> your crew only — never the client</p>
                                    <p className="text-[12px] text-white/85"><span className="text-[#E7C96A] font-semibold">Stops when:</span> everyone's confirmed, the event passes, or you stop it</p>
                                  </div>
                                )}
                                {actionStatus[idx] === 'done' ? (
                                  <div className="flex items-center gap-1.5 mt-2 text-[12px] text-emerald-400">
                                    <Check className="w-3.5 h-3.5" /> Done
                                  </div>
                                ) : actionStatus[idx] === 'failed' ? (
                                  <div className="flex items-center gap-1.5 mt-2 text-[12px] text-amber-400">
                                    <AlertCircle className="w-3.5 h-3.5" /> Couldn't complete
                                  </div>
                                ) : (
                                  <div className="flex items-center flex-wrap gap-2 mt-2.5">
                                    <button
                                      type="button"
                                      onClick={() => executeAction(idx, (msg.proposedAction.type === 'start_standing_job' && msg.proposedAction.params?.jobType === 'payment_chase') ? { ...msg.proposedAction, params: { ...msg.proposedAction.params, mode: 'auto' } } : msg.proposedAction)}
                                      disabled={actionStatus[idx] === 'running'}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] text-[#0B0F1A] text-[12px] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60"
                                    >
                                      {actionStatus[idx] === 'running' ? (
                                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Working…</>
                                      ) : (
                                        <><Check className="w-3.5 h-3.5" /> {msg.proposedAction.type === 'start_standing_job' ? (msg.proposedAction.params?.jobType === 'payment_chase' ? 'Approve & start' : 'Start autopilot') : 'Confirm'}</>
                                      )}
                                    </button>
                                    {msg.proposedAction.type === 'start_standing_job' && msg.proposedAction.params?.jobType === 'payment_chase' && (
                                      <button
                                        type="button"
                                        onClick={() => executeAction(idx, { ...msg.proposedAction, params: { ...msg.proposedAction.params, mode: 'manual' } })}
                                        disabled={actionStatus[idx] === 'running'}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/15 text-white/75 text-[12px] font-medium hover:text-white/90 transition-colors disabled:opacity-60"
                                      >
                                        Let me approve each send
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() => dismissAction(idx)}
                                      disabled={actionStatus[idx] === 'running'}
                                      className="px-3 py-1.5 rounded-lg text-[12px] text-white/50 hover:text-white/80 transition-colors disabled:opacity-60"
                                    >
                                      Not now
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}

                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white/10 border border-white/10 rounded-2xl px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                            <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                            <span className="w-2 h-2 bg-[#D4AF37] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                          </div>
                          <span className="text-sm text-gray-400">Thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <form onSubmit={sendMessage} className="p-4 border-t border-white/10">
                  <div className="flex items-end gap-2">
                    <textarea
                      ref={inputRef}
                      rows={1}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px'; }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (!isLoading && inputValue.trim()) {
                            sendMessage(e);
                            e.target.style.height = 'auto';
                          }
                        }
                      }}
                      placeholder="Ask me anything...  (Shift+Enter for a new line)"
                      className="flex-1 resize-none max-h-32 overflow-y-auto bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/20 text-[16px] sm:text-sm"
                      disabled={isLoading}
                      data-testid="ai-agent-input"
                    />
                    <Button
                      type="submit"
                      disabled={isLoading || !inputValue.trim()}
                      className="bg-gradient-to-r from-[#D4AF37] to-[#E5C76B] hover:from-[#E5C76B] hover:to-[#D4AF37] text-[#0B0F1A] rounded-xl px-4 py-3 disabled:opacity-50"
                      data-testid="ai-agent-send"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
          {canResize && ['tl', 'tr', 'bl', 'br'].map((c) => (
            <div
              key={c}
              onPointerDown={(e) => startResize(e, c)}
              data-testid={`ai-agent-resize-${c}`}
              className="hidden sm:block absolute w-4 h-4 z-10"
              style={{
                [c.includes('t') ? 'top' : 'bottom']: -3,
                [c.includes('l') ? 'left' : 'right']: -3,
                cursor: (c === 'tl' || c === 'br') ? 'nwse-resize' : 'nesw-resize',
                touchAction: 'none',
              }}
            />
          ))}
        </div>
        );
      })()}
    </>
  );
};

export default AISupportAgent;
